import type { ZoneDTO } from "../api/client";
import type {
  ShippingRateCreateInput,
  ShippingRateUpdateInput,
} from "@/modules/shipping/domain/contracts";
import {
  Button,
  FormInput,
  FormRoot,
  RHF,
  zodResolver,
  FormPrice,
} from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { formatPriceLak } from "@/shared/lib/format-price";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const RateFormSchema = z
  .object({
    zoneId: z.string().min(1, "ຕ້ອງເລືອກໂຊນ"),
    weightMinGrams: z.coerce.number().int().min(0),
    weightMaxGrams: z.coerce.number().int().min(0),
    amount: z.coerce.number().int().min(0),
  })
  .refine((data) => data.weightMinGrams < data.weightMaxGrams, {
    message: "ນ້ຳໜັກຕ່ຳຕ້ອງນ້ອຍກວ່ານ້ຳໜັກສູງ",
    path: ["weightMaxGrams"],
  });

export type RateFormValues = z.output<typeof RateFormSchema>;

type RateFormProps = {
  zones: ZoneDTO[];
  initialValues?: Partial<RateFormValues>;
  onSubmit: (values: ShippingRateCreateInput | ShippingRateUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function RateForm({
  zones,
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: RateFormProps) {
  const methods = RHF.useForm<RateFormValues>({
    resolver: zodResolver(RateFormSchema) as Resolver<RateFormValues>,
    defaultValues: {
      zoneId: "",
      weightMinGrams: 0,
      weightMaxGrams: 500,
      amount: 0,
      ...initialValues,
    },
  });

  const zoneOptions = zones.map((z) => ({ label: z.code, value: z.id }));

  const fillDev = () => {
    methods.reset({
      zoneId: zones[0]?.id ?? "",
      weightMinGrams: 0,
      weightMaxGrams: 500,
      amount: 5000,
    });
  };

  return (
    <FormRoot<RateFormValues>
      methods={methods as UseFormReturn<RateFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          zoneId: vals.zoneId,
          weightMinGrams: vals.weightMinGrams,
          weightMaxGrams: vals.weightMaxGrams,
          amount: vals.amount,
        })
      }
    >
      <div className="space-y-4" data-tourid="shipping-rate-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">ຂໍ້ມູນອັດຕາຄ່າສົ່ງ</h3>
          {onDevFill && (
            <Button type="button" variant="outline" size="sm" onClick={fillDev}>
              ເຕີມຂໍ້ມູນທົດສອບ
            </Button>
          )}
        </div>
        <div data-tourid="shipping-rate-zone">
          <label className="mb-1 block font-medium text-sm">ໂຊນ</label>
          <RHF.Controller
            name="zoneId"
            control={methods.control}
            render={({ field }) => (
              <SimpleSelect
                placeholder="ເລືອກໂຊນ"
                className="w-full"
                value={field.value}
                onValueChange={field.onChange}
                options={zoneOptions}
              />
            )}
          />
        </div>
        <div data-tourid="shipping-rate-weight" className="flex gap-4">
          <FormInput
            name="weightMinGrams"
            label="ນ້ຳໜັກຕ່ຳ (ກຣາມ)"
            type="number"
            min={0}
          />
          <FormInput
            name="weightMaxGrams"
            label="ນ້ຳໜັກສູງ (ກຣາມ)"
            type="number"
            min={0}
          />
        </div>
        <FormPrice name="amount" label="ຄ່າຂົນສົ່ງ" currencySymbol="₭" />
        <div className="flex gap-2 pt-2" data-tourid="shipping-rate-submit">
          <Button type="submit" disabled={submitting}>
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset())}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
